/**
 * Game-over standings screen (M3.5, FR-1.6/4.6). Renders the final standings by
 * rank; a tie for first shows co-winners. Derived from the authoritative
 * `game_over` state (players by score) — no controls. Presentational.
 */

import { Link } from 'react-router-dom';
import { computeStandings, type FinalOutcome, type Player } from '@jeopardy/shared';
import { Avatar } from './Avatar';
import { FINAL_PERIL } from './brand';

export interface GameOverProps {
  players: Player[];
  /**
   * When set, show a link to this game's leaderboard/summary (M9.2). Omitted in
   * dev/local where the read path isn't served — the standings still render.
   */
  gameId?: string;
  /**
   * Final Peril clue + correct response, persisted for the standings screen (#205,
   * FR-6.6 / DI-C). Present only when the game played Final Peril; absent for a game
   * ended before Final.
   */
  finalOutcome?: FinalOutcome;
}

export function GameOver({
  players,
  gameId,
  finalOutcome,
}: GameOverProps): React.JSX.Element {
  const { standings, winnerIds } = computeStandings(players);
  const nameOf = (id: string): string => players.find((p) => p.id === id)?.name ?? id;

  return (
    <div className="game-over" data-testid="game-over">
      <h2 className="game-over__title">Game over</h2>
      <p className="game-over__winner" data-testid="game-over-winner">
        {winnerIds.length === 0
          ? 'No players'
          : winnerIds.length === 1
            ? `Winner: ${nameOf(winnerIds[0]!)}`
            : `Co-winners: ${winnerIds.map(nameOf).join(' & ')}`}
      </p>
      {/* #205 (FR-6.6 / DI-C): keep the Final Peril clue + correct response on the
          standings screen. */}
      {finalOutcome && (
        <div className="game-over__final" data-testid="game-over-final">
          <div className="game-over__final-category">
            {FINAL_PERIL}
            {finalOutcome.category ? ` — ${finalOutcome.category}` : ''}
          </div>
          <div className="game-over__final-prompt">{finalOutcome.prompt}</div>
          <div className="game-over__final-response">
            The correct response was {finalOutcome.response}
          </div>
        </div>
      )}
      <ol className="game-over__standings">
        {standings.map((s) => (
          <li
            key={s.playerId}
            className="game-over__row"
            data-testid={`standing-${s.playerId}`}
          >
            <span className="game-over__rank">#{s.rank}</span>
            <Avatar name={s.name} avatarUrl={s.avatarUrl} size={24} />
            <span className="game-over__name">{s.name}</span>
            <span
              className={
                'game-over__score' + (s.score < 0 ? ' game-over__score--negative' : '')
              }
            >
              {s.score < 0 ? `-$${Math.abs(s.score)}` : `$${s.score}`}
            </span>
          </li>
        ))}
      </ol>
      {gameId && (
        <Link
          className="game-over__leaderboard-link"
          data-testid="game-over-leaderboard"
          to={`/leaderboard?gameId=${encodeURIComponent(gameId)}`}
        >
          View leaderboard
        </Link>
      )}
    </div>
  );
}
